/**
 * Admin Analytics Dashboard
 * Provides comprehensive analytics for course performance, quiz results, and system metrics
 * SUPER_ADMIN only
 */
import React, { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

function AdminAnalytics() {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Analytics data
  const [progressData, setProgressData] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);
  const [certificateData, setCertificateData] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [providerData, setProviderData] = useState(null);  // PHASE 12

  useEffect(() => {
    fetchCourses();
    fetchSystemOverview();
    fetchProviderAnalytics();  // PHASE 12
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      fetchCourseAnalytics();
    }
  }, [selectedCourseId]);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data);
        if (data.length > 0 && !selectedCourseId) {
          setSelectedCourseId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchSystemOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/analytics/system/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSystemData(data);
      }
    } catch (err) {
      console.error('Error fetching system overview:', err);
    }
  };

  const fetchProviderAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const providers = await response.json();
        const activeProviders = providers.filter(p => p.status === 'approved').length;
        const pendingProviders = providers.filter(p => p.status === 'pending').length;
        const totalAccreditations = providers.reduce((sum, p) => sum + p.accreditation_count, 0);

        setProviderData({
          total: providers.length,
          active: activeProviders,
          pending: pendingProviders,
          accreditations: totalAccreditations
        });
      }
    } catch (err) {
      console.error('Error fetching provider analytics:', err);
    }
  };

  const fetchCourseAnalytics = async () => {
    if (!selectedCourseId) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Fetch all analytics endpoints in parallel
      const [progressRes, quizRes, assignmentRes, certificateRes] = await Promise.all([
        fetch(`${API_URL}/analytics/courses/${selectedCourseId}/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/analytics/courses/${selectedCourseId}/quiz-performance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/analytics/courses/${selectedCourseId}/assignments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/analytics/courses/${selectedCourseId}/certificates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (progressRes.ok) setProgressData(await progressRes.json());
      if (quizRes.ok) setQuizData(await quizRes.json());
      if (assignmentRes.ok) setAssignmentData(await assignmentRes.json());
      if (certificateRes.ok) setCertificateData(await certificateRes.json());

    } catch (err) {
      console.error('Error fetching course analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Simple Bar Chart Component
  const BarChart = ({ data, maxValue, color = '#d4af37' }) => {
    if (!data || data.length === 0) return null;

    const max = maxValue || Math.max(...data.map(d => d.value));
    const chartHeight = 200;
    const barWidth = Math.min(60, (300 / data.length) - 10);

    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: chartHeight }}>
        {data.map((item, index) => {
          const height = (item.value / max) * (chartHeight - 20);
          return (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: barWidth }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                {item.value.toFixed(1)}%
              </div>
              <div
                style={{
                  width: '100%',
                  height: height + 'px',
                  background: `linear-gradient(180deg, ${color} 0%, ${color}dd 100%)`,
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.3s ease'
                }}
              />
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginTop: '8px',
                textAlign: 'center',
                maxWidth: '80px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Simple Pie Chart Component
  const PieChart = ({ segments, size = 150 }) => {
    if (!segments || segments.length === 0) return null;

    const total = segments.reduce((sum, seg) => sum + seg.value, 0);
    let currentAngle = -90;

    const createArc = (startAngle, endAngle, radius) => {
      const start = polarToCartesian(size / 2, size / 2, radius, endAngle);
      const end = polarToCartesian(size / 2, size / 2, radius, startAngle);
      const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
      return `M ${size / 2} ${size / 2} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
    };

    const polarToCartesian = (centerX, centerY, radius, angle) => {
      const angleInRadians = (angle * Math.PI) / 180;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians)
      };
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(0deg)' }}>
          {segments.map((segment, index) => {
            const angle = (segment.value / total) * 360;
            const path = createArc(currentAngle, currentAngle + angle, size / 2 - 5);
            const fillColor = segment.color || `hsl(${index * 137.5}, 70%, 60%)`;
            currentAngle += angle;

            return (
              <path
                key={index}
                d={path}
                fill={fillColor}
                stroke="#fff"
                strokeWidth="2"
              />
            );
          })}
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {segments.map((segment, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  background: segment.color || `hsl(${index * 137.5}, 70%, 60%)`
                }}
              />
              <div style={{ fontSize: '0.85rem', color: '#333' }}>
                {segment.label}: <strong>{segment.value}</strong>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
        color: '#1a1a1a',
        padding: '24px 32px',
        borderRadius: '8px',
        marginBottom: '32px',
        boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem', fontWeight: '700' }}>
          📊 Analytics Dashboard
        </h1>
        <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>
          Comprehensive course performance and system metrics
        </p>
      </div>

      {/* Course Selector */}
      <div style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '32px',
        border: '2px solid #e0e0e0'
      }}>
        <label style={{ display: 'block', fontWeight: '700', marginBottom: '12px', fontSize: '1.05rem', color: '#333' }}>
          Select Course
        </label>
        <select
          value={selectedCourseId || ''}
          onChange={(e) => setSelectedCourseId(parseInt(e.target.value))}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '1rem',
            border: '2px solid #d4af37',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          {courses.map(course => (
            <option key={course.id} value={course.id}>
              {course.title}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '48px', fontSize: '1.1rem', color: '#666' }}>
          Loading analytics...
        </div>
      )}

      {error && (
        <div style={{
          padding: '24px',
          background: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '2px solid #e57373'
        }}>
          {error}
        </div>
      )}

      {/* System Overview */}
      {systemData && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#d4af37',
            marginBottom: '16px',
            borderBottom: '3px solid #d4af37',
            paddingBottom: '8px'
          }}>
            System Overview
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {[
              { label: 'Total Users', value: systemData.total_users, icon: '👥' },
              { label: 'Total Courses', value: systemData.total_courses, icon: '📚' },
              { label: 'Certificates Issued', value: systemData.total_certificates_issued, icon: '🎓' },
              { label: 'Quiz Attempts', value: systemData.total_quiz_attempts, icon: '✍️' },
              { label: 'Assignment Submissions', value: systemData.total_assignment_submissions, icon: '📝' }
            ].map((metric, index) => (
              <div key={index} style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{metric.icon}</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37', marginBottom: '4px' }}>
                  {metric.value}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Provider Analytics - PHASE 12 */}
      {providerData && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#d4af37',
            marginBottom: '16px',
            borderBottom: '3px solid #d4af37',
            paddingBottom: '8px'
          }}>
            Provider Analytics (PHASE 12)
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {[
              { label: 'Total Providers', value: providerData.total, icon: '🏢', color: '#2196f3' },
              { label: 'Active Providers', value: providerData.active, icon: '✓', color: '#4caf50' },
              { label: 'Pending Approval', value: providerData.pending, icon: '⏳', color: '#ff9800' },
              { label: 'Accreditations Issued', value: providerData.accreditations, icon: '🎓', color: '#d4af37' }
            ].map((metric, index) => (
              <div key={index} style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '8px',
                border: `2px solid ${metric.color}`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{metric.icon}</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: metric.color, marginBottom: '4px' }}>
                  {metric.value}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && selectedCourseId && (
        <>
          {/* Course Progress Analytics */}
          {progressData && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#d4af37',
                marginBottom: '16px',
                borderBottom: '3px solid #d4af37',
                paddingBottom: '8px'
              }}>
                Course Progress
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Completion Rate</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4caf50' }}>
                    {progressData.completion_rate}%
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #2196f3'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Active Learners</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2196f3' }}>
                    {progressData.active_learners}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #ff9800'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Completed Learners</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#ff9800' }}>
                    {progressData.completed_learners}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #9c27b0'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Modules Required</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#9c27b0' }}>
                    {progressData.modules_required}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quiz Performance Analytics */}
          {quizData && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#d4af37',
                marginBottom: '16px',
                borderBottom: '3px solid #d4af37',
                paddingBottom: '8px'
              }}>
                Quiz Performance
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Average Score</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4caf50' }}>
                    {quizData.average_score}%
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #2196f3'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Pass Rate</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2196f3' }}>
                    {quizData.pass_rate}%
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #ff9800'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Average Attempts</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#ff9800' }}>
                    {quizData.average_attempts}
                  </div>
                </div>
              </div>

              {quizData.quiz_breakdown && quizData.quiz_breakdown.length > 0 && (
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0'
                }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '1.2rem', color: '#333' }}>
                    Quiz Breakdown
                  </h3>
                  <BarChart
                    data={quizData.quiz_breakdown.map(q => ({
                      label: q.title,
                      value: q.average_score
                    }))}
                    maxValue={100}
                    color="#4caf50"
                  />
                  <div style={{ marginTop: '24px' }}>
                    {quizData.quiz_breakdown.map((quiz, index) => (
                      <div key={index} style={{
                        padding: '12px',
                        background: index % 2 === 0 ? '#f9f9f9' : '#fff',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gap: '16px',
                        alignItems: 'center'
                      }}>
                        <div style={{ fontWeight: '600', color: '#333' }}>{quiz.title}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Score: <strong>{quiz.average_score}%</strong></div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Pass: <strong>{quiz.pass_rate}%</strong></div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Attempts: <strong>{quiz.attempt_count}</strong></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Assignment Analytics */}
          {assignmentData && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#d4af37',
                marginBottom: '16px',
                borderBottom: '3px solid #d4af37',
                paddingBottom: '8px'
              }}>
                Assignment Analytics
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Submission Rate</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4caf50' }}>
                    {assignmentData.submission_rate}%
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #2196f3'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Graded Rate</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2196f3' }}>
                    {assignmentData.graded_rate}%
                  </div>
                </div>
              </div>

              {assignmentData.assignment_breakdown && assignmentData.assignment_breakdown.length > 0 && (
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#333' }}>
                    Assignment Breakdown
                  </h3>
                  {assignmentData.assignment_breakdown.map((assignment, index) => (
                    <div key={index} style={{
                      padding: '16px',
                      background: index % 2 === 0 ? '#f9f9f9' : '#fff',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333', marginBottom: '8px' }}>
                        {assignment.title}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Submissions: <strong>{assignment.submissions}</strong>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Graded: <strong>{assignment.graded}</strong>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Avg Grade: <strong>{assignment.average_grade}/100</strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Certificate Analytics */}
          {certificateData && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#d4af37',
                marginBottom: '16px',
                borderBottom: '3px solid #d4af37',
                paddingBottom: '8px'
              }}>
                Certificate Analytics
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Issue Rate</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4caf50' }}>
                    {certificateData.issue_rate}%
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #2196f3'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Issued</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2196f3' }}>
                    {certificateData.issued_count}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '2px solid #ff9800'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Not Issued</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#ff9800' }}>
                    {certificateData.not_issued_count}
                  </div>
                </div>
              </div>

              <div style={{
                background: '#fff',
                padding: '24px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <PieChart
                  segments={[
                    { label: 'Issued', value: certificateData.issued_count, color: '#4caf50' },
                    { label: 'Not Issued', value: certificateData.not_issued_count, color: '#ff9800' }
                  ]}
                  size={180}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminAnalytics;
